import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import resourceMonitoringService from "../../../services/resourceMonitoring.service";
import { generalActions } from "../../../store/actions";
import { headerDate } from "./constants";
import {
  calculateCapacityTillMidnight,
  convertToMinutes,
  incrementTime,
  tConverter,
} from "../../../helpers/utils";
import searchService from "../../../store/services/search.service";

dayjs.extend(quarterOfYear);

const ResourcePlannerContext = createContext();

const ResourcePlannerProvider = ({ children }) => {
  const dispatch = useDispatch();

  // UI State
  const [tabView, setTabView] = useState("month");
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [openPopover, setIsOpenPopover] = useState(null);
  const [detail, setDetail] = useState(null);
  const [headerValue, setHeaderValue] = useState("");

  // Period Navigation State
  const [currentPeriod, setCurrentPeriod] = useState({
    startDate: null,
    endDate: null,
    displayText: "",
  });
  const [periodStartDate, setPeriodStartDate] = useState(null);

  // Calendar Data
  const [calendarData, setCalendarData] = useState([]);
  const [calendarHeader, setCalendarHeader] = useState([]);
  const [useDetails, setUserDetails] = useState([]);

  // Filter State
  const [project, setProject] = useState({ name: "All Projects" });
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterShifts, setFilterShifts] = useState([]);
  const [filterCapacity, setFilterCapacity] = useState([]);
  const [todayTrigger, setTodayTrigger] = useState(false);

  // Shift Assignment State
  const [assignedResources, setAssignedResources] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedValue, setSelectedValue] = useState("Saved Shift");
  const [selectedShift, setSelectedShift] = useState("");
  const [name, setname] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [editShift, setEditShift] = useState(false);
  const [addShiftView, setAddShiftView] = useState(false);

  // Shifts & Tasks
  const [shifts, setShifts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskLoader, setTaskLoader] = useState(true);
  const [taskId, setTaskId] = useState(null);
  const [scopeId, setScopeId] = useState(null);

  // Refs
  const debounceRef = useRef(null);

  // Project Search State
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const projectSearchDebounceRef = useRef(null);

  // Memoized filter count
  const filterCount = useMemo(() => {
    let count = 0;
    if (filterStartDate) count++;
    if (filterUsers.length > 0) count++;
    if (filterShifts.length > 0) count++;
    if (filterCapacity.length > 0) count++;
    return count;
  }, [filterStartDate, filterUsers, filterShifts, filterCapacity]);

  /**
   * Transform user data from API response
   */
  const transformUserData = useCallback((records) => {
    return records.map((item) => ({
      id: item.resource.id,
      name: item.resource.name,
      imageUrl: item.resource.image,
      totalHours: item?.totalCapacity,
      workLoad: item?.allocatedCapacity,
    }));
  }, []);

  /**
   * Transform calendar data based on view type
   */
  const transformCalendarData = useCallback((data, type) => {
    const calendar = [];

    if (type.toLowerCase() === "week" || type.toLowerCase() === "month") {
      data.forEach((item) => {
        const arr = Object.values(item.availability).map((shift) => [{
          totalHours: shift.totalCapacity,
          workLoad: shift.allocatedCapacity,
          timeOffs: shift.timeOffs,
          taskCount: shift.tasksCount,
          data: shift.shifts,
        }]);
        calendar.push(arr);
      });
    } else if (type.toLowerCase() === "day") {
      const arr = [];
      data.forEach((item) => {
        const innerArr = Object.values(item.availability).map((shift) => {
          if (shift.shifts.length === 0 && shift.timeOffs.length === 0) {
            return [{
              id: null,
              shifts: "",
              workLoad: -1,
              totalHours: 0,
              startTime: "",
              endTime: "",
            }];
          }

          const dataArr = [];
          shift.shifts.forEach((i) => {
            dataArr.push({
              id: i.id,
              shifts: i.name,
              totalHours: i.totalCapacity,
              workLoad: i.allocatedCapacity,
              startTime: i.startTime,
              startDate: shift.period,
              endTime: i.endTime,
              data: shift.shifts,
              timeOffs: shift.timeOffs,
            });
          });

          shift.timeOffs.forEach((i) => {
            dataArr.push({
              id: i.id,
              shifts: i.name,
              totalHours: i.duration,
              workLoad: i.duration,
              startTime: i.startTime,
              endTime: i.endTime,
              timeoff: true,
              data: shift.shifts,
              timeOffs: shift.timeOffs,
            });
          });

          return dataArr;
        });
        arr.push(innerArr);
      });
      calendar.push(arr);
    } else if (type.toLowerCase() === "hour") {
      data.forEach((item) => {
        const inObj = {};
        Object.values(item.availability).forEach((date) => {
          if (date.shifts.length > 0) {
            date.shifts.forEach((shift) => {
              const data = [];
              let { startTime, endTime } = shift;
              const isShiftCrossingMidnight = convertToMinutes(startTime) > convertToMinutes(endTime);
              startTime = tConverter(startTime);
              endTime = tConverter(endTime);
              let start = startTime;

              do {
                data.push({
                  start: start,
                  colorPercentage: (Number(startTime.split(":")[1].split(" ")[0]) / 60) * 100,
                  colorType: (shift.duration / shift.capacity) * 100,
                  totalHours: isShiftCrossingMidnight
                    ? calculateCapacityTillMidnight(start, "12:00 AM")
                    : shift.totalCapacity,
                  workingHours: shift.allocatedCapacity,
                  data: [shift],
                  timeOffs: shift.timeOffs || [],
                });

                start = incrementTime(start);
              } while (
                isShiftCrossingMidnight
                  ? start !== "12:00 AM"
                  : Number(start.split(":")[0]) !== Number(endTime.split(":")[0])
              );

              inObj[`${date.period}${startTime.split(":")[0]}${startTime.split(" ")[1]}`] = data;
            });
          }
        });
        calendar.push(inObj);
      });
    }

    return type.toLowerCase() === "day" ? calendar[0] : calendar;
  }, []);

  /**
   * Generate calendar headers based on view type
   */
  const generateCalendarHeaders = useCallback((data, type) => {
    const header = [];
    const availability = Object.values(data[0].availability);

    switch (type.toLowerCase()) {
      case "week":
        if (availability.length > 0) {
          availability.forEach((item) => {
            const periodParts = item.period.split(" - ");
            header.push({
              name: periodParts[0],
              date: `${periodParts[1].split("-")[2]}-${periodParts[2].split("-")[2]} ${new Date(
                periodParts[2]
              ).toLocaleString("default", { month: "short" })}`,
            });
          });
        }
        break;

      case "month":
        if (availability.length > 0) {
          availability.forEach((item) => {
            header.push({
              name: new Date(`${item.period}-01`).toLocaleString("default", { month: "short" }),
            });
          });
        }
        break;

      case "day":
        if (availability.length > 0) {
          const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
          availability.forEach((item) => {
            const d = new Date(item.period);
            header.push({
              name: days[d.getDay()],
              date: d.getDate(),
            });
          });
        }
        break;

      case "hour":
        if (availability.length > 0 && Array.isArray(headerDate)) {
          availability.forEach((dateItem) => {
            headerDate.forEach((hour) => {
              header.push({ date: dateItem.period, name: hour.name });
            });
          });
        }
        break;

      default:
        break;
    }

    return header;
  }, []);

  /**
   * Calculate period display text and date range from API response
   */
  const calculatePeriodFromResponse = useCallback((data, view) => {
    if (!data || data.length === 0) return null;

    const availability = data[0]?.availability || [];
    if (availability.length === 0) return null;

    let startDate, endDate, displayText;

    switch (view.toLowerCase()) {
      case "month": {
        // Period format: "2026-01"
        const firstPeriod = availability[0].period; // "2026-01"
        const lastPeriod = availability[availability.length - 1].period; // "2026-12"

        // Extract year from first and last month
        const startYear = parseInt(firstPeriod.split("-")[0], 10);
        const endYear = parseInt(lastPeriod.split("-")[0], 10);

        // Create proper date objects
        startDate = dayjs(`${startYear}-01-01`).startOf('year').toISOString();
        endDate = dayjs(`${endYear}-12-31`).endOf('year').toISOString();

        displayText = startYear === endYear ? `${startYear}` : `${startYear} - ${endYear}`;
        break;
      }

      case "week": {
        // Period format: "Week 1 - 2026-01-01 - 2026-01-07"
        const firstWeek = availability[0].period.split(" - ");
        const lastWeek = availability[availability.length - 1].period.split(" - ");

        // Extract dates from "Week X - startDate - endDate" format
        const quarterStartDate = dayjs(firstWeek[1].trim());
        const quarterEndDate = dayjs(lastWeek[2].trim());

        startDate = quarterStartDate.startOf('day').toISOString();
        endDate = quarterEndDate.endOf('day').toISOString();

        const startMonth = quarterStartDate.format("MMM");
        const endMonth = quarterEndDate.format("MMM");
        const year = quarterStartDate.year();

        displayText = `${startMonth} - ${endMonth} ${year}`;
        break;
      }

      case "day": {
        // Period format: "2026-02-01"
        const firstDay = dayjs(availability[0].period);
        const lastDay = dayjs(availability[availability.length - 1].period);

        const month = firstDay.format("MMMM");
        const year = firstDay.year();

        startDate = firstDay.startOf('month').toISOString();
        endDate = lastDay.endOf('month').toISOString();

        displayText = `${month} ${year}`;
        break;
      }

      default:
        return null;
    }

    return { startDate, endDate, displayText };
  }, []);

  /**
   * Navigate to previous period
   */
  const goToPreviousPeriod = useCallback(() => {
    if (!currentPeriod.startDate) return;

    let newStartDate;

    switch (tabView.toLowerCase()) {
      case "month": {
        // Go to previous year - get the start of previous year
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.subtract(1, "year").startOf("year");
        break;
      }

      case "week": {
        // Go to previous quarter - get the start of previous quarter
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.subtract(3, "month").startOf("month");
        break;
      }

      case "day": {
        // Go to previous month - get the start of previous month
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.subtract(1, "month").startOf("month");
        break;
      }

      default:
        return;
    }

    // Format as ISO UTC for API
    setPeriodStartDate(newStartDate.format('YYYY-MM-DD'));
  }, [currentPeriod, tabView]);

  /**
   * Navigate to next period
   */
  const goToNextPeriod = useCallback(() => {
    if (!currentPeriod.startDate) return;

    let newStartDate;

    switch (tabView.toLowerCase()) {
      case "month": {
        // Go to next year - get the start of next year
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.add(1, "year").startOf("year");
        break;
      }

      case "week": {
        // Go to next quarter - get the start of next quarter
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.add(3, "month").startOf("month");
        break;
      }

      case "day": {
        // Go to next month - get the start of next month
        const currentStart = dayjs(currentPeriod.startDate);
        newStartDate = currentStart.add(1, "month").startOf("month");
        break;
      }

      default:
        return;
    }

    // Format as ISO UTC for API
    setPeriodStartDate(newStartDate.format('YYYY-MM-DD'));
  }, [currentPeriod, tabView]);

  /**
   * Go to current period (Today)
   */
  const goToToday = useCallback(() => {
    setPeriodStartDate(null); // Reset to let backend handle current period
    setTodayTrigger((prev) => !prev);
  }, []);

  /**
   * Fetch calendar data with debouncing
   */
  const fetchCalendarData = useCallback(
    async (showLoader = true) => {
      if (showLoader) {
        setIsCalendarLoading(true);
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          const params = {
            view: tabView,
            projectId: project.name !== "All Projects" ? project.id : null,
            shifts: filterShifts,
            taskId,
            scopeId,
            resources: filterUsers.map((u) => u.id),
            capacity: filterCapacity,
            // Use periodStartDate for navigation, filterStartDate for custom filters
            startDate: filterStartDate || periodStartDate,
            endDate: filterEndDate,
          };

          const data = await resourceMonitoringService.fetchCalendarData(params);

          if (!data || data.length === 0) {
            setCalendarData([]);
            setCalendarHeader([]);
            setUserDetails([]);
            setCurrentPeriod({ startDate: null, endDate: null, displayText: "" });
            return;
          }

          // Calculate and set period from response
          const period = calculatePeriodFromResponse(data, tabView);
          if (period) {
            setCurrentPeriod(period);
          }

          // Transform data
          const users = transformUserData(data);
          const headers = generateCalendarHeaders(data, tabView);
          const calendar = transformCalendarData(data, tabView);

          setUserDetails(users);
          setCalendarHeader(headers);
          setCalendarData(calendar);
        } catch (error) {
          dispatch(
            generalActions.showToast({
              title: error.message || "Failed to fetch calendar data",
              type: "error",
            })
          );
          setCalendarData([]);
          setCalendarHeader([]);
          setUserDetails([]);
        } finally {
          setIsCalendarLoading(false);
        }
      }, 300);
    },
    [
      tabView,
      project,
      filterShifts,
      taskId,
      scopeId,
      filterUsers,
      filterCapacity,
      filterStartDate,
      filterEndDate,
      periodStartDate,
      calculatePeriodFromResponse,
      transformUserData,
      generateCalendarHeaders,
      transformCalendarData,
      dispatch,
    ]
  );

  /**
   * Fetch shifts list
   */
  const fetchShiftsList = useCallback(async () => {
    try {
      const data = await resourceMonitoringService.fetchShiftsList();
      setShifts(data);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error.message || "Failed to fetch shifts",
          type: "error",
        })
      );
    }
  }, [dispatch]);

  /**
   * Fetch tasks by resource ID
   */
  const fetchTasksByResourceId = useCallback(async () => {
    if (!useDetails?.[detail?.userIndex]) {
      setTasks([]);
      return;
    }

    setTaskLoader(true);

    try {
      let startDateParam = null;
      let endDateParam = null;

      if (tabView.toLowerCase() === "week") {
        const shiftData = calendarData[detail.userIndex]?.[detail.ind]?.[0]?.data?.[0];
        if (shiftData) {
          startDateParam = dayjs(shiftData.startDate.split("T")[0]).startOf('day').toISOString();
          endDateParam = dayjs(shiftData.endDate.split("T")[0]).endOf('day').toISOString();
        }
      } else if (tabView.toLowerCase() === "month") {
        const requiredData = calendarData[detail.userIndex]?.[detail.ind]?.[0]?.data;
        if (requiredData?.[0]) {
          startDateParam = dayjs(requiredData[0].startDate.split("T")[0]).startOf('day').toISOString();
          endDateParam = dayjs(requiredData[requiredData.length - 1].endDate.split("T")[0]).endOf('day').toISOString();
        }
      } else if (tabView.toLowerCase() === "day") {
        const dayData = calendarData[detail.userIndex]?.[detail.ind]?.[0];
        if (dayData && dayData.startDate) {
          startDateParam = dayjs(dayData.startDate).startOf('day').toISOString();
          endDateParam = dayjs(dayData.startDate).endOf('day').toISOString();
        }
      }

      const params = {
        view: tabView,
        startDate: startDateParam,
        endDate: endDateParam,
        ...(project && project.name !== "All Projects" ? { projectId: project.id } : {}),
      };

      const resourceId = useDetails[detail.userIndex].id;
      const data = await resourceMonitoringService.fetchTasksByResourceId(resourceId, params);

      setTasks(data.tasks?.length > 0 ? data : []);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error.message || "Failed to fetch tasks",
          type: "error",
        })
      );
      setTasks([]);
    } finally {
      setTaskLoader(false);
    }
  }, [useDetails, detail, tabView, calendarData, project, dispatch]);

  /**
   * Search projects with debouncing
   */
  const searchProjects = useCallback(async (searchTerm = "") => {
    // Clear previous debounce
    if (projectSearchDebounceRef.current) {
      clearTimeout(projectSearchDebounceRef.current);
    }

    setProjectsLoading(true);

    projectSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await searchService.Projects(searchTerm);

        if (response && Array.isArray(response)) {
          setProjectsList(response);
        } else {
          setProjectsList([]);
        }
      } catch (error) {
        setProjectsList([]);
      } finally {
        setProjectsLoading(false);
      }
    }, 300);
  }, [dispatch]);

  useEffect(() => {
    fetchCalendarData(true);
  }, [fetchCalendarData]);

  useEffect(() => {
    fetchShiftsList();
  }, [fetchShiftsList]);

  useEffect(() => {
    if (detail) {
      fetchTasksByResourceId();
    }
  }, [detail, fetchTasksByResourceId]);

  useEffect(() => {
    setPeriodStartDate(null);
    setCurrentPeriod({ startDate: null, endDate: null, displayText: "" });
  }, [tabView]);

  useEffect(() => {
    if (filterStartDate) {
      setPeriodStartDate(null);
    }
  }, [filterStartDate]);

  // Fetch initial project on mount
  useEffect(() => {
    const fetchInitialProject = async () => {
      // Only fetch if project is not set or is "All Projects"
      if (!project.id || project.name === "All Projects") {
        try {
          const response = await searchService.Projects("");

          if (response && response.length > 0) {
            // Set projects list
            setProjectsList(response);
            
            // Set first project as default
            setProject({
              name: response[0].name,
              id: response[0].id,
            });
          }
        } catch (error) {
          console.error("Failed to fetch initial project:", error);
        }
      }
    };

    fetchInitialProject();
  }, []); // Run only once on mount

  const contextValue = useMemo(
    () => ({
      calendarData,
      calendarHeader,
      useDetails,
      headerValue,
      tabView,
      isCalendarLoading,
      openPopover,
      detail,
      addShiftView,
      project,
      filterStartDate,
      filterEndDate,
      filterUsers,
      filterShifts,
      filterCapacity,
      filterCount,
      todayTrigger,
      assignedResources,
      selectedDays,
      startDate,
      endDate,
      selectedValue,
      selectedShift,
      name,
      startTime,
      endTime,
      isDefault,
      editShift,
      shifts,
      tasks,
      taskLoader,
      taskId,
      scopeId,
      setTabView,
      setIsOpenPopover,
      setDetail,
      setAddShiftView,
      setProject,
      setFilterStartDate,
      setFilterEndDate,
      setFilterUsers,
      setFilterShifts,
      setFilterCapacity,
      setTodayTrigger,
      setAssignedResources,
      setSelectedDays,
      setStartDate,
      setEndDate,
      setSelectedValue,
      setSelectedShift,
      setname,
      setStartTime,
      setEndTime,
      setIsDefault,
      setEditShift,
      setTaskId,
      setScopeId,
      setHeaderValue,
      fetchCalendarData,
      fetchTasksByResourceId,
      createTimeOff: resourceMonitoringService.createTimeOff,
      deleteTimeOff: resourceMonitoringService.deleteTimeOff,
      deleteShift: resourceMonitoringService.deleteShift,
      assignShift: resourceMonitoringService.assignShift,
      currentPeriod,
      goToPreviousPeriod,
      goToNextPeriod,
      goToToday,
      projectsList,
      projectsLoading,
      searchProjects,
    }),
    [
      calendarData,
      calendarHeader,
      useDetails,
      headerValue,
      tabView,
      isCalendarLoading,
      openPopover,
      detail,
      addShiftView,
      project,
      filterStartDate,
      filterEndDate,
      filterUsers,
      filterShifts,
      filterCapacity,
      filterCount,
      todayTrigger,
      assignedResources,
      selectedDays,
      startDate,
      endDate,
      selectedValue,
      selectedShift,
      name,
      startTime,
      endTime,
      isDefault,
      editShift,
      shifts,
      tasks,
      taskLoader,
      taskId,
      scopeId,
      fetchCalendarData,
      fetchTasksByResourceId,
      currentPeriod,
      goToPreviousPeriod,
      goToNextPeriod,
      goToToday,
      projectsList,
      projectsLoading,
      searchProjects,
    ]
  );

  return (
    <ResourcePlannerContext.Provider value={contextValue}>
      {children}
    </ResourcePlannerContext.Provider>
  );
};

export { ResourcePlannerContext, ResourcePlannerProvider };
